import { Phone, ArrowRight, Shield, Award, Briefcase } from 'lucide-react';
import Marquee from 'react-fast-marquee';
import { trackPhoneCall, trackQuoteClick } from '@/utils/analytics';
import { useIsHydrating } from '@/hooks/useHydration';

const stats = [
  { icon: Briefcase, value: '600+', label: 'Projects Completed' },
  { icon: Award, value: '10+', label: 'Years Experience' },
  { icon: Shield, value: 'IBEW', label: 'Journeyman' },
];

const marqueeWords = [
  'LICENSED & INSURED', 'IBEW JOURNEYMAN', 'COMMERCIAL', 'RESIDENTIAL',
  'INDUSTRIAL', 'NASHVILLE', 'LICENSED & INSURED', '600+ PROJECTS',
];

function HeroBackground() {
  return (
    <div className="absolute inset-0">
      <img
        srcSet="https://images.unsplash.com/photo-1758101755915-462eddc23f57?w=480&q=75&fm=webp 480w,
                https://images.unsplash.com/photo-1758101755915-462eddc23f57?w=800&q=75&fm=webp 800w,
                https://images.unsplash.com/photo-1758101755915-462eddc23f57?w=1200&q=75&fm=webp 1200w,
                https://images.unsplash.com/photo-1758101755915-462eddc23f57?w=1600&q=75&fm=webp 1600w"
        sizes="100vw"
        src="https://images.unsplash.com/photo-1758101755915-462eddc23f57?w=1200&q=75&fm=webp"
        alt="electrician Nashville TN working on electrical panel"
        className="w-full h-full object-cover"
        width={1600}
        height={1200}
        fetchPriority="high"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent" />
    </div>
  );
}

function HeroCTAs() {
  return (
    <div className="flex flex-wrap gap-4 mb-16">
      <a href="#contact" data-testid="hero-cta-quote" onClick={() => trackQuoteClick('hero')}
        className="inline-flex items-center gap-2 bg-yellow-400 text-black font-heading font-bold text-sm uppercase tracking-wider px-8 py-4 border border-black hover:-translate-y-1 transition-transform">
        Get a Project Consultation <ArrowRight className="w-4 h-4" />
      </a>
      <a href="tel:+16159615930" data-testid="hero-cta-phone" onClick={() => trackPhoneCall('hero')}
        className="inline-flex items-center gap-2 bg-white/10 backdrop-blur text-white font-heading font-bold text-sm uppercase tracking-wider px-8 py-4 border border-white/30 hover:bg-white/20 transition-colors">
        <Phone className="w-4 h-4" /> (615) 961-5930
      </a>
    </div>
  );
}

function HeroStats() {
  return (
    <div className="flex flex-wrap gap-10">
      {stats.map((stat) => (
        <div key={stat.label} className="flex items-center gap-3" data-testid={`hero-stat-${stat.value.toLowerCase().replace(/[+\s]/g, '')}`}>
          <stat.icon className="w-5 h-5 text-yellow-400" />
          <div>
            <p className="font-display text-3xl text-white">{stat.value}</p>
            <p className="font-body text-xs uppercase tracking-wider text-white/50">{stat.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function HeroMarquee({ isHydrating }) {
  if (isHydrating) {
    return (
      <div className="absolute bottom-0 left-0 right-0 bg-yellow-400 border-t-2 border-black overflow-hidden">
        <div className="flex">
          {marqueeWords.map((word, i) => (
            <span key={`${word}-${i}`} className="font-heading font-black text-xs uppercase tracking-[0.2em] text-black px-8 py-2.5 inline-block whitespace-nowrap">
              {`${word} \u2022`}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="absolute bottom-0 left-0 right-0 bg-yellow-400 border-t-2 border-black">
      <Marquee speed={50} gradient={false}>
        {marqueeWords.map((word, i) => (
          <span key={`${word}-${i}`} className="font-heading font-black text-xs uppercase tracking-[0.2em] text-black px-8 py-2.5 inline-block">
            {`${word} \u2022`}
          </span>
        ))}
      </Marquee>
    </div>
  );
}

export default function HeroSection() {
  const isHydrating = useIsHydrating();

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      <HeroBackground />
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-24">
        <p className="font-body text-xs uppercase tracking-[0.25em] text-yellow-400 mb-4">
          Your Trusted Electrical Company in Nashville, Tennessee
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-black text-white tracking-tighter uppercase leading-[0.9] max-w-4xl mb-6">
          Licensed Electrician in Nashville, TN
          <span className="text-yellow-400"> — Evolution Electric</span>
        </h1>
        <p className="font-body text-base sm:text-lg text-white/70 max-w-xl mb-10 leading-relaxed">
          Licensed by the State of Tennessee with IBEW Journeyman Experience, we are Evolution Electric, a trusted electrician Nashville TN serving homeowners and businesses. With over a decade of experience, we deliver safe, reliable, and code-compliant electrical work you can count on.
        </p>
        <HeroCTAs />
        <HeroStats />
      </div>
      <HeroMarquee isHydrating={isHydrating} />
    </section>
  );
}
